/**
 * Merge multiple Firestore pending-expense query snapshots without retaining
 * docs that have left a query (e.g. after categorization).
 *
 * Cardholders listen on both assignedUserId and cardLastFour queries. A single
 * shared Map that only `.set`s docs will keep stale pending rows forever after
 * Save, so the charge looks unsaved until a full remount.
 */

export type PendingExpenseLike = { id: string };

/** Union every query's current docs. Uses Map.forEach so CRA's ES5 emit stays valid. */
export function unionPendingExpenseSources<T extends PendingExpenseLike>(
  sources: Map<string, Map<string, T>>,
): Map<string, T> {
  const merged = new Map<string, T>();
  sources.forEach(sourceDocs => {
    sourceDocs.forEach((row, id) => {
      merged.set(id, row);
    });
  });
  return merged;
}

/** Replace one query's contribution and rebuild the union across all sources. */
export function mergePendingExpenseSources<T extends PendingExpenseLike>(
  sources: Map<string, Map<string, T>>,
  sourceKey: string,
  docs: T[],
): Map<string, T> {
  const nextSource = new Map<string, T>();
  docs.forEach(doc => {
    nextSource.set(doc.id, doc);
  });
  sources.set(sourceKey, nextSource);
  return unionPendingExpenseSources(sources);
}
